import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft } from "lucide-react";

const FrenchGrammarTest = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [testStarted, setTestStarted] = useState(false);

  const questions = [
    // A1 Grammar Questions
    {
      level: "A1",
      question: "Marie ____ française.",
      options: [
        { id: "a", text: "est", correct: true },
        { id: "b", text: "es", correct: false },
        { id: "c", text: "être", correct: false },
        { id: "d", text: "sont", correct: false }
      ],
      explanation: "Verb 'être' (to be) - 3rd person singular: elle est"
    },
    {
      level: "A1",
      question: "J'____ un chat et un chien.",
      options: [
        { id: "a", text: "a", correct: false },
        { id: "b", text: "ai", correct: true },
        { id: "c", text: "as", correct: false },
        { id: "d", text: "ont", correct: false }
      ],
      explanation: "Verb 'avoir' (to have) - 1st person singular: j'ai"
    },
    {
      level: "A1",
      question: "Nous ____ au cinéma ce soir.",
      options: [
        { id: "a", text: "va", correct: false },
        { id: "b", text: "vas", correct: false },
        { id: "c", text: "allons", correct: true },
        { id: "d", text: "vont", correct: false }
      ],
      explanation: "Verb 'aller' (to go) - 1st person plural: nous allons"
    },
    {
      level: "A1",
      passage: "Bonjour! Je m'appelle Sophie. J'habite à Lyon. J'ai 20 ans et j'étudie l'anglais à l'université.",
      question: "Où habite Sophie?",
      options: [
        { id: "a", text: "À Paris", correct: false },
        { id: "b", text: "À Lyon", correct: true },
        { id: "c", text: "À Marseille", correct: false },
        { id: "d", text: "À Nice", correct: false }
      ],
      explanation: "Sophie says 'J'habite à Lyon' which means 'I live in Lyon'"
    },
    {
      level: "A1",
      passage: "Paul aime le football. Il joue tous les mercredis avec ses amis dans le parc.",
      question: "Quand Paul joue-t-il au football?",
      options: [
        { id: "a", text: "Le lundi", correct: false },
        { id: "b", text: "Le mercredi", correct: true },
        { id: "c", text: "Le samedi", correct: false },
        { id: "d", text: "Tous les jours", correct: false }
      ],
      explanation: "'tous les mercredis' means 'every Wednesday'"
    },
    // A2 Questions
    {
      level: "A2",
      question: "Hier, je ____ au restaurant avec mes amis.",
      options: [
        { id: "a", text: "vais", correct: false },
        { id: "b", text: "suis allé", correct: true },
        { id: "c", text: "irai", correct: false },
        { id: "d", text: "allais", correct: false }
      ],
      explanation: "Passé composé with 'aller': je suis allé(e)"
    },
    {
      level: "A2",
      question: "Elle ____ ses devoirs quand le téléphone a sonné.",
      options: [
        { id: "a", text: "fait", correct: false },
        { id: "b", text: "a fait", correct: false },
        { id: "c", text: "faisait", correct: true },
        { id: "d", text: "fera", correct: false }
      ],
      explanation: "Imparfait for ongoing action in the past: elle faisait"
    },
    {
      level: "A2",
      question: "Je voudrais ____ pain, s'il vous plaît.",
      options: [
        { id: "a", text: "de", correct: false },
        { id: "b", text: "du", correct: true },
        { id: "c", text: "de la", correct: false },
        { id: "d", text: "des", correct: false }
      ],
      explanation: "Partitive article with masculine noun: du pain"
    },
    {
      level: "A2",
      passage: "La semaine dernière, Claire est allée au marché. Elle a acheté des fruits, des légumes et du fromage. Puis elle est rentrée chez elle pour préparer le déjeuner.",
      question: "Qu'est-ce que Claire a fait après le marché?",
      options: [
        { id: "a", text: "Elle est allée au cinéma", correct: false },
        { id: "b", text: "Elle est rentrée chez elle", correct: true },
        { id: "c", text: "Elle a travaillé", correct: false },
        { id: "d", text: "Elle a téléphoné à une amie", correct: false }
      ],
      explanation: "'Puis elle est rentrée chez elle' means 'Then she went back home'"
    },
    {
      level: "A2",
      passage: "Thomas adore voyager. Il a déjà visité l'Italie, l'Espagne et le Portugal. L'année prochaine, il veut aller au Japon.",
      question: "Où Thomas veut-il aller l'année prochaine?",
      options: [
        { id: "a", text: "En Italie", correct: false },
        { id: "b", text: "En Espagne", correct: false },
        { id: "c", text: "Au Japon", correct: true },
        { id: "d", text: "Au Portugal", correct: false }
      ],
      explanation: "'L'année prochaine, il veut aller au Japon' means 'Next year, he wants to go to Japan'"
    },
    // B1 Questions
    {
      level: "B1",
      question: "Si j'avais de l'argent, je ____ en voyage.",
      options: [
        { id: "a", text: "vais", correct: false },
        { id: "b", text: "irai", correct: false },
        { id: "c", text: "irais", correct: true },
        { id: "d", text: "suis allé", correct: false }
      ],
      explanation: "Conditional present after 'si + imperfect': j'irais"
    },
    {
      level: "B1",
      question: "Il faut que tu ____ plus attention en classe.",
      options: [
        { id: "a", text: "fais", correct: false },
        { id: "b", text: "fasses", correct: true },
        { id: "c", text: "faire", correct: false },
        { id: "d", text: "ferais", correct: false }
      ],
      explanation: "Subjunctive after 'il faut que': que tu fasses"
    },
    {
      level: "B1",
      question: "Cette maison a été ____ par un architecte célèbre.",
      options: [
        { id: "a", text: "construire", correct: false },
        { id: "b", text: "construisant", correct: false },
        { id: "c", text: "construit", correct: false },
        { id: "d", text: "construite", correct: true }
      ],
      explanation: "Passive voice with past participle agreement: construite (feminine)"
    },
    {
      level: "B1",
      passage: "Le télétravail présente des avantages et des inconvénients. D'un côté, il permet d'économiser du temps de transport et d'être plus flexible. D'un autre côté, certains employés se sentent isolés et ont du mal à séparer vie professionnelle et vie personnelle.",
      question: "Selon le texte, quel est un avantage du télétravail?",
      options: [
        { id: "a", text: "On gagne du temps de transport", correct: true },
        { id: "b", text: "On se sent moins isolé", correct: false },
        { id: "c", text: "Le travail est plus facile", correct: false },
        { id: "d", text: "On travaille moins", correct: false }
      ],
      explanation: "'Il permet d'économiser du temps de transport' means 'it allows saving commute time'"
    },
    {
      level: "B1",
      passage: "Pour bien apprendre une langue étrangère, il ne suffit pas de mémoriser du vocabulaire. Il est essentiel de pratiquer régulièrement, de s'immerger dans la culture et d'oser parler même si on fait des erreurs.",
      question: "D'après le texte, que faut-il faire pour apprendre une langue?",
      options: [
        { id: "a", text: "Seulement mémoriser", correct: false },
        { id: "b", text: "Pratiquer et s'immerger dans la culture", correct: true },
        { id: "c", text: "Ne jamais faire d'erreurs", correct: false },
        { id: "d", text: "Éviter de parler", correct: false }
      ],
      explanation: "The text emphasizes practice, immersion, and speaking despite making mistakes"
    },
    // B2 Questions
    {
      level: "B2",
      question: "Bien qu'il ____ tard, nous avons décidé de sortir.",
      options: [
        { id: "a", text: "est", correct: false },
        { id: "b", text: "était", correct: false },
        { id: "c", text: "soit", correct: true },
        { id: "d", text: "sera", correct: false }
      ],
      explanation: "Subjunctive after 'bien que': bien qu'il soit"
    },
    {
      level: "B2",
      question: "Je l'ai rencontré ____ mon séjour en France.",
      options: [
        { id: "a", text: "pendant", correct: false },
        { id: "b", text: "durant", correct: false },
        { id: "c", text: "au cours de", correct: true },
        { id: "d", text: "depuis", correct: false }
      ],
      explanation: "'Au cours de' is more formal and used for specific periods"
    },
    {
      level: "B2",
      question: "À condition qu'il ____ beau temps, nous irons à la plage.",
      options: [
        { id: "a", text: "fait", correct: false },
        { id: "b", text: "fasse", correct: true },
        { id: "c", text: "fera", correct: false },
        { id: "d", text: "faisait", correct: false }
      ],
      explanation: "Subjunctive after 'à condition que': qu'il fasse"
    },
    {
      level: "B2",
      passage: "Le changement climatique représente l'un des défis majeurs de notre époque. Bien que les scientifiques multiplient les avertissements, les mesures prises restent insuffisantes. La transition énergétique nécessite non seulement des investissements massifs, mais également un changement profond des mentalités et des comportements individuels et collectifs.",
      question: "Selon le texte, que faut-il pour réussir la transition énergétique?",
      options: [
        { id: "a", text: "Uniquement de l'argent", correct: false },
        { id: "b", text: "Des investissements et un changement de mentalités", correct: true },
        { id: "c", text: "Ignorer les scientifiques", correct: false },
        { id: "d", text: "Attendre que ça passe", correct: false }
      ],
      explanation: "The text mentions both 'investissements massifs' and 'changement profond des mentalités'"
    },
    {
      level: "B2",
      passage: "L'intelligence artificielle suscite autant d'espoirs que d'inquiétudes. Si elle promet des avancées considérables dans des domaines comme la médecine et l'éducation, elle soulève également des questions éthiques fondamentales concernant la vie privée, l'emploi et l'autonomie humaine. Un débat démocratique s'impose pour encadrer son développement.",
      question: "Pourquoi l'intelligence artificielle nécessite-t-elle un débat démocratique?",
      options: [
        { id: "a", text: "Elle est trop chère", correct: false },
        { id: "b", text: "Elle pose des questions éthiques importantes", correct: true },
        { id: "c", text: "Elle ne fonctionne pas bien", correct: false },
        { id: "d", text: "Personne ne l'utilise", correct: false }
      ],
      explanation: "The text mentions 'questions éthiques fondamentales' as the reason for democratic debate"
    }
  ];

  const handleAnswer = (questionIndex, optionId) => {
    setAnswers({ ...answers, [questionIndex]: optionId });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const calculateResults = () => {
    let a1Score = 0;
    let a2Score = 0;
    let b1Score = 0;
    let b2Score = 0;

    questions.forEach((question, index) => {
      const selectedOption = answers[index];
      const correctOption = question.options.find(opt => opt.correct);
      
      if (selectedOption === correctOption.id) {
        switch (question.level) {
          case "A1":
            a1Score++;
            break;
          case "A2":
            a2Score++;
            break;
          case "B1":
            b1Score++;
            break;
          case "B2":
            b2Score++;
            break;
          default:
            break;
        }
      }
    });

    return { a1Score, a2Score, b1Score, b2Score };
  };

  const determineLevel = () => {
    const scores = calculateResults();
    const total = scores.a1Score + scores.a2Score + scores.b1Score + scores.b2Score;
    const percentage = (total / questions.length) * 100;

    if (scores.b2Score >= 3 && scores.b1Score >= 3 && percentage >= 75) {
      return { level: "B2", description: "Upper Intermediate - You have a good command of complex grammar structures" };
    } else if (scores.b1Score >= 3 && scores.a2Score >= 3 && percentage >= 60) {
      return { level: "B1", description: "Intermediate - You can handle most grammar structures with confidence" };
    } else if (scores.a2Score >= 3 && scores.a1Score >= 3 && percentage >= 45) {
      return { level: "A2", description: "Elementary - You can use basic grammar structures with confidence" };
    } else {
      return { level: "A1", description: "Beginner - You are starting to build the foundations of the language" };
    }
  };

  const currentQ = questions[currentQuestion];

  if (!testStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
        <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Button
              variant="ghost"
              className="text-white hover:text-white/80 mb-4"
              onClick={() => navigate("/")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="test-title">
              French Level Test
            </h1>
            <p className="text-xl text-white/90">Assess your level from A1 to B2</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="shadow-2xl">
            <CardContent className="p-8 sm:p-12">
              <h2 className="text-3xl font-bold text-[#04539F] mb-6">How much French do you know?</h2>
              <div className="space-y-4 text-gray-700 mb-8">
                <p className="text-lg">Complete our free level test and discover your level.</p>
                <div className="flex gap-2 justify-center my-6">
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">A1</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">A2</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">B1</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">B2</span>
                </div>
                <div className="bg-[#04539F]/10 rounded-lg p-6 space-y-2">
                  <ul className="list-disc list-inside space-y-2">
                    <li>The test will take 10 to 15 minutes</li>
                    <li>Select the correct answer for each question</li>
                    <li>Take your time - there's no time limit</li>
                    <li>Discover your level after completing the test</li>
                  </ul>
                </div>
              </div>
              <Button
                size="lg"
                className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full py-6 text-lg"
                onClick={() => setTestStarted(true)}
                data-testid="start-test-btn"
              >
                Start Level Test
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showResults) {
    const result = determineLevel();
    const scores = calculateResults();
    const totalCorrect = scores.a1Score + scores.a2Score + scores.b1Score + scores.b2Score;

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
        <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-4xl font-bold text-white">Level Test Completed!</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="shadow-2xl mb-8">
            <CardContent className="p-8 sm:p-12">
              <div className="text-center mb-8">
                <div className="inline-block bg-[#04539F] text-white rounded-full px-12 py-6 mb-4">
                  <span className="text-6xl font-bold">{result.level}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{result.description}</h2>
                <p className="text-xl text-gray-600">You answered {totalCorrect} out of {questions.length} questions correctly</p>
              </div>

              <div className="bg-[#04539F]/10 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-[#04539F] mb-4">Your Answers</h3>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {questions.map((q, idx) => {
                    const userAnswer = answers[idx];
                    const correctAnswer = q.options.find(opt => opt.correct);
                    const isCorrect = userAnswer === correctAnswer.id;
                    
                    return (
                      <div key={idx} className="bg-white rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                            {isCorrect ? '✓' : '✗'}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 mb-2">{q.question}</p>
                            {!isCorrect && (
                              <p className="text-sm text-gray-600">
                                <span className="font-semibold">Correct answer:</span> {correctAnswer.text}
                                <br />
                                <span className="text-gray-500 italic">{q.explanation}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCurrentQuestion(0);
                    setAnswers({});
                    setShowResults(false);
                    setTestStarted(false);
                  }}
                  className="rounded-full"
                >
                  Retake Test
                </Button>
                <Button
                  className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                  onClick={() => navigate("/contact")}
                >
                  Contact Berni
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-white">French Level Test</h1>
            <div className="text-white text-sm">
              Question {currentQuestion + 1} of {questions.length}
            </div>
          </div>
          <div className="mt-4 bg-white/20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <div className="mb-6">
              {currentQ.passage && (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <p className="text-gray-800 leading-relaxed italic">{currentQ.passage}</p>
                </div>
              )}
              <h3 className="text-2xl font-bold text-[#04539F] mb-6">{currentQ.question}</h3>
            </div>

            <div className="space-y-3 mb-8">
              {currentQ.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleAnswer(currentQuestion, option.id)}
                  className={`w-full text-left p-5 rounded-lg border-2 transition-all text-lg ${
                    answers[currentQuestion] === option.id
                      ? 'border-[#04539F] bg-[#04539F]/10'
                      : 'border-gray-200 hover:border-[#04539F]/50'
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <div className="flex items-center">
                    <div className={`w-7 h-7 rounded-full border-2 mr-4 flex items-center justify-center ${
                      answers[currentQuestion] === option.id
                        ? 'border-[#04539F] bg-[#04539F] text-white'
                        : 'border-gray-300'
                    }`}>
                      {answers[currentQuestion] === option.id && '✓'}
                    </div>
                    <span className="font-medium text-gray-800">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleNext}
                disabled={!answers[currentQuestion]}
                className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full px-8"
                data-testid="next-btn"
              >
                {currentQuestion === questions.length - 1 ? 'Finish Test' : 'Next'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FrenchGrammarTest;
